import { getApiB2bApplicationByIdFormState } from '/api/sdk.gen';
import type { GetApiB2bApplicationByIdFormStateResponses } from '/api';

import type { AppDispatch } from '/business/shared/state/store';
import resetReductionEstimate from '/business/shared/state/actions/initiative-estimate/resetReductionEstimate';
import ValidationError from '/shared/errors/ValidationError';
import normaliseError, { toNormaliseErrorInput } from '/shared/functions/normaliseError';

type FormStateResponse = GetApiB2bApplicationByIdFormStateResponses[200];
type MetaPartialFormStateResponse = Extract<FormStateResponse, { revalidatedForMeta: string[] }>;
type BasketPartialFormStateResponse = Extract<FormStateResponse, { revalidatedFor: string[] }>;
type FullFormStateResponse = Extract<FormStateResponse, { state: object }>;

const isMetaPartialFormState = (formStateData: FormStateResponse): formStateData is MetaPartialFormStateResponse =>
  'revalidatedForMeta' in formStateData;

const isBasketPartialFormState = (formStateData: FormStateResponse): formStateData is BasketPartialFormStateResponse =>
  'revalidatedFor' in formStateData;

const isFullFormState = (formStateData: FormStateResponse): formStateData is FullFormStateResponse =>
  'state' in formStateData;

const getFormState =
  (
    id: number | string | null = null,
    basketQuestionsPartialLoading: string[] = [],
    metaPartialLoading: string[] = [],
  ) =>
  async (dispatch: AppDispatch) => {
    try {
      if (!id) {
        throw new Error('No data sent to get form state');
      }
      dispatch({ type: 'BASKET_REPLACE_STARTED', timestamp: Date.now() });
      dispatch({ type: 'GET_FORM_STATE_STARTED', applicationId: id, timestamp: Date.now() });
      dispatch({ type: 'GET_USER_DEFINED_INITIATIVES_STARTED', timestamp: Date.now() });

      const { data: formStateData } = await getApiB2bApplicationByIdFormState({
        path: { id: String(id) },
        query: {
          dyn: 1,
          revalApiKey: basketQuestionsPartialLoading.length > 0 ? basketQuestionsPartialLoading.join(',') : undefined,
          metaPartialLoad: metaPartialLoading.length > 0 ? metaPartialLoading.join(',') : undefined,
        },
      });

      if (!formStateData) {
        throw new Error('No form state response');
      }

      if (metaPartialLoading.length > 0) {
        if (!isMetaPartialFormState(formStateData)) {
          throw new Error('Invalid meta partial form state response');
        }
        const { revalidatedForMeta, revalidatedState, retrieved_at: retrievedAt } = formStateData;
        dispatch({
          type: 'BASKET_REPLACE_META_PARTIAL_FULFILLED',
          applicationId: id,
          revalidatedForMeta,
          retrievedAt,
          revalidatedState,
        });
        dispatch({ type: 'GET_FORM_STATE_RESET', timestamp: Date.now() });
        dispatch(resetReductionEstimate());
        dispatch({ type: 'GET_USER_DEFINED_INITIATIVES_RESET', timestamp: Date.now() });

        return formStateData;
      }
      if (basketQuestionsPartialLoading.length > 0) {
        if (!isBasketPartialFormState(formStateData)) {
          throw new Error('Invalid basket partial form state response');
        }
        const {
          revalidatedFor,
          revalidatedState,
          revalidatedQuestionAnswerStructured,
          emissions,
          retrieved_at: retrievedAt,
        } = formStateData;

        dispatch({
          type: 'GET_FORM_STATE_PARTIAL_FULFILLED',
          id,
          emissions,
        });
        dispatch({
          type: 'BASKET_REPLACE_PARTIAL_FULFILLED',
          revalidatedState,
          applicationId: id,
          revalidatedQuestionAnswerStructured,
          retrievedAt,
          revalidatedFor,
        });
        dispatch(resetReductionEstimate());
        dispatch({ type: 'GET_USER_DEFINED_INITIATIVES_RESET', timestamp: Date.now() });

        return formStateData;
      }

      if (!isFullFormState(formStateData)) {
        throw new Error('Invalid form state response');
      }
      const {
        emissions,
        averages,
        paymentAwaitingProcessing,
        offset,
        applicationState,
        quote,
        userDefinedInitiatives,
        questionAnswerStructured,
        retrieved_at: retrievedAt,
      } = formStateData;
      const { items, meta, stateAppId } = formStateData.state;
      dispatch({
        type: 'GET_FORM_STATE_FULFILLED',
        id,
        emissions,
        averages,
        paymentAwaitingProcessing,
        offset,
        applicationState,
        quote,
        basketItems: items,
        basketMeta: meta,
        questionAnswerStructured,
      });

      dispatch({ type: 'BASKET_REPLACE_FULFILLED', items, meta, stateAppId, questionAnswerStructured, retrievedAt });
      dispatch({ type: 'GET_USER_DEFINED_INITIATIVES_FULFILLED', userDefinedInitiatives });
      await dispatch(resetReductionEstimate());
      return formStateData;
    } catch (error) {
      const normalisedError = normaliseError(toNormaliseErrorInput(error), 'GET_FORM_STATE');
      const isInitialFormStateRequest = basketQuestionsPartialLoading.length === 0 && metaPartialLoading.length === 0;

      if (isInitialFormStateRequest && normalisedError.code === 'ERRNOTFOUND') {
        dispatch({ type: 'GET_FORM_STATE_RESET', timestamp: Date.now() });
        dispatch({ type: 'BASKET_REPLACE_REJECTED', timestamp: Date.now() });
        dispatch({ type: 'GET_USER_DEFINED_INITIATIVES_RESET', timestamp: Date.now() });
        return;
      }

      if (error instanceof ValidationError) {
        dispatch({ type: 'GET_FORM_STATE_REJECTED', applicationId: id, error });
      } else {
        dispatch({
          type: 'GET_FORM_STATE_REJECTED',
          applicationId: id,
          error: normalisedError,
        });
      }
      dispatch({ type: 'BASKET_REPLACE_REJECTED', error, timestamp: Date.now() });
      dispatch({ type: 'GET_USER_DEFINED_INITIATIVES_REJECTED', error, timestamp: Date.now() });

      throw error;
    }
  };

export default getFormState;
